/**
 * Environment Configuration
 *
 * This file contains all environment-specific configuration values.
 * These values are public (bundled into the client) and safe to commit to source control.
 *
 * SECURITY GUIDELINES:
 *
 * ✅ SAFE TO STORE HERE:
 * - Public API URLs (apiBaseUrl)
 * - Client-side SDK keys (Sentry DSN, Datadog client tokens, LaunchDarkly client IDs)
 * - Project/App IDs for analytics (Clarity, RudderStack write keys)
 * - Any value that will be exposed in the client bundle anyway
 *
 * ❌ DO NOT STORE HERE:
 * - Server-side API keys or secrets
 * - Private keys or certificates
 * - Database credentials
 * - OAuth secrets
 * - Any credentials that should remain server-side only
 *
 * Note: All values here will be bundled into the client-side JavaScript
 * and can be inspected by users in production builds.
 */

/**
 * Squire API contract version (a Stripe-style *dated version label*, not a
 * date of "old code"). `2022-09-02` is the contract that introduced the
 * brand-level service-id model. Clients that send it opt into that contract;
 * with no `Squire-Version` header the backend falls back to its much older
 * `defaultVersion` (2019-12-24), which uses a different service-id space.
 *
 * This is env-invariant (same value on qa/staging/prod) — it pins the API
 * contract, not an environment. It is applied per-call on the booking service
 * and cart surface (mirroring water-on-web's `SERVICE_ID_V2_HEADER_MIXIN`),
 * NOT as a global default header.
 *
 * ⚠️ Contract coupling: booking service-selection matches the cart's
 * `items[].services[].id` against the services-list `id`, relying on this
 * contract keeping both in one id-space across cart write + re-read. It is a
 * fixed pin — bump it deliberately if the backend ships a newer service-id
 * contract; do not expect it to auto-advance.
 */
export const SQUIRE_API_VERSION = '2022-09-02';

/**
 * Ready-to-spread request headers that opt a call into SQUIRE_API_VERSION.
 * Applied only on the booking + waitlist calls that deal in the brand-level
 * service-id contract — NOT app-wide (most endpoints run on the default
 * contract and must not send this).
 */
export const BRAND_SERVICE_VERSION_HEADERS = {
  'Squire-Version': SQUIRE_API_VERSION,
} as const;

/**
 * Legal targets for the public standby surface (SQR-14760).
 *
 * Deliberately NOT `config.termsUrl`/`config.privacyUrl`: those point at the
 * consumer app's documents, while a walk-in joining a shop's standby list
 * agrees to the enterprise terms. Legal sign-off on the exact targets is still
 * pending, so both live here as the single source to change.
 */
export const STANDBY_LEGAL_URLS = {
  terms: 'https://getsquire.com/legal/enterprise-terms-of-service',
  privacy: 'https://getsquire.com/privacy',
} as const;

/**
 * Squire's Reserve with Google conversion partner id, issued by Google and the
 * same on every environment — it identifies Squire to Google, not a deployment.
 * Public by design: it is sent in the browser's conversion POST. SQR-16105.
 */
export const RESERVE_WITH_GOOGLE_PARTNER_ID = '10000116';

export type Environment = 'qa' | 'staging' | 'production';

export interface EnvironmentConfig {
  // API Configuration
  apiBaseUrl: string;

  // Web (SvelteKit www-*.getsquire.com) — used for hosted flows opened in WebView
  webUrl: string;

  // Flagship web (S3/CloudFront) — the widget iframe opens it directly
  flagshipWebUrl: string;

  // Sentry - Error tracking
  sentryDsn: string;

  // Datadog - Application monitoring
  datadogClientToken: string;
  datadogAppId: string;
  // datadogTracker.web inits RUM from the first entry when embedded; empty
  // keeps flagship's own app (SQR-13814).
  widgetDatadogApps: readonly { applicationId: string; clientToken: string }[];

  // Microsoft Clarity - User behavior analytics
  clarityProjectId: string;
  clarityWebProjectId: string;

  // RudderStack - Customer data platform
  rudderstackWriteKey: string;
  rudderstackDataPlaneUrl: string;

  // LaunchDarkly - Feature flags
  launchDarklyKey: string; // For native (iOS/Android)
  launchDarklyWebId: string; // For web

  // Stripe
  stripePublicKey: string;
  stripeMerchantId: string;

  // Google Sign In
  googleOAuthClientId: string;

  // Apple Sign In (web only)
  appleWebClientId: string;
  appleWebRedirectUri: string;

  // Reserve with Google - conversion reporting endpoint (Google's live
  // collector in production, its debug collector everywhere else)
  reserveWithGoogleConversionUrl: string;

  // Fluent Ads
  fluentApiKey: string; // Native SDK API key (from Fluent account manager)
  fluentReferer: string; // Native SDK referer — typically the app's domain/bundle
  fluentPartnerId: string; // Web SDK partner ID used in FlowContainer asset URLs
  fluentPlacementId: string; // Web SDK placement ID used in FlowContainer asset URLs
  fluentCanadianPlacementId: string; // Placement ID for Canadian residents
  fluentCanadianReferer: string; // Referer for Canadian residents
  fluentFrenchCanadianPlacementId: string; // Placement ID for French Canadian residents
  fluentFrenchCanadianReferer: string; // Referer for French Canadian residents
}

/**
 * Environment-specific configurations
 *
 * All values here are public and will be bundled into the client-side code.
 * Add new environments or configuration keys here as needed.
 */
export const environments: Record<Environment, EnvironmentConfig> = {
  qa: {
    // API
    apiBaseUrl: 'https://api-qa-1.getsquire.com',

    // Web
    webUrl: 'https://www-qa-1.getsquire.com/',
    flagshipWebUrl: 'https://squire-mobile-flagship-qa-1.getsquire.com/',

    // Sentry
    sentryDsn:
      'https://56a07ebf7e0a168681a0f4758806b802@o72192.ingest.us.sentry.io/4510267495874560',

    // Datadog
    datadogClientToken: 'pub91d97f7c30a692844e13f38f4c33aca6',
    datadogAppId: '50398a20-3843-4802-9d9a-c1c8f20edba4',
    widgetDatadogApps: [
      {
        applicationId: '22e7eae2-ab83-4bd6-9f32-efcff3932405',
        clientToken: 'pub72b0d994524f146667d77423b1e48946',
      },
    ],

    // Clarity (SANDBOX values for QA)
    clarityProjectId: 'tybl7j155o',
    clarityWebProjectId: 'txcb242kem',

    // RudderStack (SANDBOX values for QA)
    rudderstackWriteKey: '34TKgQJb7oRyaS1qSoHBA7TMkfU',
    rudderstackDataPlaneUrl: 'https://getsquirezjvnp.dataplane.rudderstack.com',

    // LaunchDarkly (QA-specific)
    launchDarklyKey: 'mob-ce521b11-d426-4b56-8845-3fbd2d4ebada',
    launchDarklyWebId: '6436efb9bc840f13563ad642',

    // Stripe
    stripePublicKey: 'pk_test_WFMOwR069KZvJoKilYNKsQNR',
    stripeMerchantId: 'merchant.com.applications.Squir-App',

    // Google Sign In
    googleOAuthClientId:
      '869442095238-6b54r3tseeedph3mjmb3cb6mjeu5hsg5.apps.googleusercontent.com',

    // Apple Sign In (web only) - TODO: fill in once Apple Service ID is configured
    appleWebClientId: 'com.getsquire.web',
    appleWebRedirectUri: 'https://www-qa-1.getsquire.com',

    // Reserve with Google
    reserveWithGoogleConversionUrl:
      'https://www.google.com/maps/conversion/debug/collect',

    // Fluent Ads
    fluentApiKey: 'a3872279-3dd9-4ccd-bcdf-119ed2267db2',
    fluentPartnerId: 'a3872279-3dd9-4ccd-bcdf-119ed2267db2',
    fluentPlacementId: 'b5544bd0-bbef-45c6-8896-93ebb8e91af4',
    fluentCanadianPlacementId: '54b9ce50-dc56-4218-8522-8e3bdca7f1b8',
    fluentFrenchCanadianPlacementId: '436c213f-bf82-4ba1-b070-9e14f4c8614c',
    fluentReferer: 'dev_5a4128e6-2a1e-4d83-855e-a5b05c86cb20',
    fluentCanadianReferer: 'dev_aabb1d45-ac2c-43bc-9d76-eedd5f68c2ac',
    fluentFrenchCanadianReferer: 'dev_c81aeb43-3387-4aa6-9d98-d4fc3a512266',
  },

  staging: {
    // API
    apiBaseUrl: 'https://api-staging-1.getsquire.com',

    // Web
    webUrl: 'https://www-staging-1.getsquire.com/',
    flagshipWebUrl: 'https://squire-mobile-flagship-staging-1.getsquire.com/',

    // Sentry
    sentryDsn:
      'https://56a07ebf7e0a168681a0f4758806b802@o72192.ingest.us.sentry.io/4510267495874560',

    // Datadog
    datadogClientToken: 'pub91d97f7c30a692844e13f38f4c33aca6',
    datadogAppId: '50398a20-3843-4802-9d9a-c1c8f20edba4',
    widgetDatadogApps: [
      {
        applicationId: '22e7eae2-ab83-4bd6-9f32-efcff3932405',
        clientToken: 'pub72b0d994524f146667d77423b1e48946',
      },
    ],

    // Clarity (SANDBOX values for Staging)
    clarityProjectId: 'tybl7j155o',
    clarityWebProjectId: 'txcb242kem',

    // RudderStack (SANDBOX values for Staging)
    rudderstackWriteKey: '34TKgQJb7oRyaS1qSoHBA7TMkfU',
    rudderstackDataPlaneUrl: 'https://getsquirezjvnp.dataplane.rudderstack.com',

    // LaunchDarkly (Staging-specific)
    launchDarklyKey: 'mob-7fe71188-b7d9-4840-a43b-684cec6dcddf',
    launchDarklyWebId: '6436efa503de2013267cb48b',

    // Stripe
    stripePublicKey: 'pk_test_WFMOwR069KZvJoKilYNKsQNR',
    stripeMerchantId: 'merchant.com.applications.Squir-App',

    // Google Sign In
    googleOAuthClientId:
      '869442095238-6b54r3tseeedph3mjmb3cb6mjeu5hsg5.apps.googleusercontent.com',

    // Apple Sign In (web only) - TODO: fill in once Apple Service ID is configured
    appleWebClientId: 'com.getsquire.web',
    appleWebRedirectUri: 'https://www-staging-1.getsquire.com',

    // Reserve with Google
    reserveWithGoogleConversionUrl:
      'https://www.google.com/maps/conversion/debug/collect',

    // Fluent Ads
    fluentApiKey: 'a3872279-3dd9-4ccd-bcdf-119ed2267db2',
    fluentPartnerId: 'a3872279-3dd9-4ccd-bcdf-119ed2267db2',
    fluentPlacementId: 'b5544bd0-bbef-45c6-8896-93ebb8e91af4',
    fluentCanadianPlacementId: '54b9ce50-dc56-4218-8522-8e3bdca7f1b8',
    fluentFrenchCanadianPlacementId: '436c213f-bf82-4ba1-b070-9e14f4c8614c',
    fluentReferer: 'dev_5a4128e6-2a1e-4d83-855e-a5b05c86cb20',
    fluentCanadianReferer: 'dev_aabb1d45-ac2c-43bc-9d76-eedd5f68c2ac',
    fluentFrenchCanadianReferer: 'dev_c81aeb43-3387-4aa6-9d98-d4fc3a512266',
  },

  production: {
    // API
    apiBaseUrl: 'https://api.getsquire.com',
    // Web
    webUrl: 'https://www.getsquire.com/',
    flagshipWebUrl: 'https://squire-mobile-flagship-prod-1.getsquire.com/',

    // Sentry
    sentryDsn:
      'https://56a07ebf7e0a168681a0f4758806b802@o72192.ingest.us.sentry.io/4510267495874560',

    // Datadog
    datadogClientToken: 'pub91d97f7c30a692844e13f38f4c33aca6',
    datadogAppId: '50398a20-3843-4802-9d9a-c1c8f20edba4',
    widgetDatadogApps: [
      {
        applicationId: '22e7eae2-ab83-4bd6-9f32-efcff3932405',
        clientToken: 'pub72b0d994524f146667d77423b1e48946',
      },
    ],

    // Clarity (Production values)
    clarityProjectId: 'tyxjxbw5lv',
    clarityWebProjectId: 'tybjum3e4o',

    // RudderStack (Production values)
    rudderstackWriteKey: '34TKlB6d6McnSwDk49qjpMDH71C',
    rudderstackDataPlaneUrl: 'https://rudder-prod.getsquire.com',

    // LaunchDarkly (Production-specific)
    launchDarklyKey: 'mob-5ce50c1d-9f62-423e-850a-fdc9a7852a51',
    launchDarklyWebId: '6436c833d00d0c1325b654a0',

    // Stripe
    stripePublicKey: 'pk_live_i7yVGwE1iFznsnhYqqpFNxmg',
    stripeMerchantId: 'merchant.com.applications.Squir-App',

    // Google Sign In
    googleOAuthClientId:
      '869442095238-6b54r3tseeedph3mjmb3cb6mjeu5hsg5.apps.googleusercontent.com',

    // Apple Sign In (web only) - TODO: fill in once Apple Service ID is configured
    appleWebClientId: 'com.getsquire.web',
    appleWebRedirectUri: 'https://getsquire.com',

    // Reserve with Google
    reserveWithGoogleConversionUrl:
      'https://www.google.com/maps/conversion/collect',

    // Fluent Ads
    fluentApiKey: 'a3872279-3dd9-4ccd-bcdf-119ed2267db2',
    fluentPartnerId: 'a3872279-3dd9-4ccd-bcdf-119ed2267db2',
    fluentPlacementId: 'b5544bd0-bbef-45c6-8896-93ebb8e91af4',
    fluentCanadianPlacementId: '54b9ce50-dc56-4218-8522-8e3bdca7f1b8',
    fluentFrenchCanadianPlacementId: '436c213f-bf82-4ba1-b070-9e14f4c8614c',
    fluentReferer: 'prod_5a4128e6-2a1e-4d83-855e-a5b05c86cb20',
    fluentCanadianReferer: 'prod_aabb1d45-ac2c-43bc-9d76-eedd5f68c2ac',
    fluentFrenchCanadianReferer: 'prod_c81aeb43-3387-4aa6-9d98-d4fc3a512266',
  },
};

/**
 * Get configuration for a specific environment
 */
export function getEnvironmentConfigByName(
  env: Environment,
): EnvironmentConfig {
  return environments[env];
}

/**
 * Validate that an environment name is valid
 */
export function isValidEnvironment(env: string): env is Environment {
  return env === 'qa' || env === 'staging' || env === 'production';
}
